import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	type AcceptInviteFormValues,
	acceptInviteSchema,
	joinInviteSchema,
} from "@/features/auth/schemas/accept-invite-schema";

export interface AcceptInviteViolations {
	name?: string;
	password?: string;
}

export interface AcceptInviteFormProps {
	error?: string;
	loading?: boolean;
	violations?: AcceptInviteViolations;
	userExists?: boolean;
	onSubmit: (values: AcceptInviteFormValues) => void;
}

export function AcceptInviteForm({
	error,
	loading = false,
	violations,
	userExists = false,
	onSubmit,
}: AcceptInviteFormProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<AcceptInviteFormValues>({
		resolver: zodResolver(userExists ? joinInviteSchema : acceptInviteSchema),
		defaultValues: { name: "", password: "", passwordConfirm: "" },
	});
	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

	return (
		<form
			noValidate
			className="accept-invite-form"
			onSubmit={handleSubmit(onSubmit)}
		>
			{!!error && (
				<p role="alert" className="form-error-alert">
					{error}
				</p>
			)}

			{!userExists && (
				<div className="form-group">
					<Label htmlFor="name">Nome completo</Label>
					<Input
						id="name"
						autoComplete="name"
						placeholder="Como devemos te chamar?"
						aria-invalid={!!errors.name || !!violations?.name}
						{...register("name")}
					/>
					{(errors.name || violations?.name) && (
						<p className="form-error">
							{errors.name?.message ?? violations?.name}
						</p>
					)}
				</div>
			)}

			<div className="form-group">
				<Label htmlFor="password">
					{userExists ? "Senha da sua conta" : "Senha"}
				</Label>
				<div className="password-field">
					<Input
						id="password"
						type={showPassword ? "text" : "password"}
						autoComplete={userExists ? "current-password" : "new-password"}
						placeholder={
							userExists
								? "Digite a senha da sua conta"
								: "Mínimo de 8 caracteres"
						}
						aria-invalid={!!errors.password || !!violations?.password}
						className="password-field__input"
						{...register("password")}
					/>
					<button
						type="button"
						onClick={() => setShowPassword((value) => !value)}
						aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
						className="password-field__reveal"
					>
						{showPassword ? <EyeOff /> : <Eye />}
					</button>
				</div>
				{(errors.password || violations?.password) && (
					<p className="form-error">
						{errors.password?.message ?? violations?.password}
					</p>
				)}
			</div>

			{!userExists && (
				<div className="form-group">
					<Label htmlFor="passwordConfirm">Confirme a senha</Label>
					<div className="password-field">
						<Input
							id="passwordConfirm"
							type={showPasswordConfirm ? "text" : "password"}
							autoComplete="new-password"
							placeholder="Digite a senha novamente"
							aria-invalid={!!errors.passwordConfirm}
							className="password-field__input"
							{...register("passwordConfirm")}
						/>
						<button
							type="button"
							onClick={() => setShowPasswordConfirm((value) => !value)}
							aria-label={
								showPasswordConfirm ? "Ocultar senha" : "Mostrar senha"
							}
							className="password-field__reveal"
						>
							{showPasswordConfirm ? <EyeOff /> : <Eye />}
						</button>
					</div>
					{errors.passwordConfirm && (
						<p className="form-error">{errors.passwordConfirm.message}</p>
					)}
				</div>
			)}

			<Button
				type="submit"
				className="accept-invite-form__submit"
				disabled={loading}
			>
				{loading && <Loader2 className="auth-spinner" />}
				{userExists ? "Entrar na obra" : "Aceitar convite"}
			</Button>
		</form>
	);
}
